import { useCallback, useEffect, useState } from "react";
import { CheckBox, Column, EntryForm, Input } from "../components/EntryForm";
import Page from "../components/Page"
import { useUserProvider } from "../providers/UserProvider";
import { useFormik } from "formik";
import * as Yup from "yup";
import { validations } from "../lib/form";
import { useModalProvider } from "../providers/ModalProvider";
import { useHistory } from "react-router-dom";
import responseProcessor from "../lib/responseProcessor";
import MediaUploader from "../components/MediaUploader";
import DataSelector from "../components/DataSelector";
import { AddAppResource } from "../services/api/app";
import { GetCategory, UpdateCategory } from "../services/api/category";
import LinkedContentList from "./LinkedContentsList";
import { useLoaderProvider } from "../providers/LoaderProvider";

const defaultValues = {
  name: "",
  image_url: "",
  tags: [],
  sort_order: "",
  active: false,
  media_metadata: {},
}

const CategoryDetail = ({
  newCategory,
  match
}) => {

  const [isLoading, setIsLoading] = useState(false);
  const [currentDetail, setCurrentDetail] = useState(defaultValues)
  const [metadata, setMetadata] = useState({})
  const [linkedContent, setLinkedContent] = useState({
    loading: false,
    data: [],
    count: 0,
  })

  const { user } = useUserProvider();
  const { showModal } = useModalProvider();
  const { doSetShowLoadingModal } = useLoaderProvider()
  const history = useHistory()

  const uuid = newCategory ? user.currentApp() : match.params.uuid;

  const currentAction = newCategory ? "New" : "Edit";
  const currentUrl = newCategory ? "/categories/new" : `/categories/${uuid}`;

  let links = [
    { name: "Home", url: "/" },
    { name: "Categories List", url: "/categories" },
    { name: `${currentAction} Category`, url: currentUrl },
  ];

  const handleSuccess = () => {
    setIsLoading(false);
    doSetShowLoadingModal(false)
    history.push("/categories")
  };
  
  const handleError = () => {
    setIsLoading(false);
    doSetShowLoadingModal(false)
  };

  const responseProcessorProxy = (response, additionalOnSuccess) => {
    responseProcessor({
      response,
      showModal,
      onSuccess: () => {
        setIsLoading(false)
        handleSuccess();
        if (additionalOnSuccess) {
          additionalOnSuccess();
        }
      },
      onError: handleError,
    });
  };

  const formik = useFormik({
    initialValues: currentDetail,
    enableReinitialize: true,
    validationSchema: () =>
      Yup.lazy((values) => {
        let schema = {
          name: validations.str_req_60,
          image_url: validations.str_255,
          sort_order: validations.sort_order,
          active: validations.bool
        };

        return Yup.object().shape(schema);
      }),
    onSubmit: async (values) => {
      setIsLoading(true)
      doSetShowLoadingModal(true)

      if (newCategory) {
        AddAppResource(uuid, "categories", user.idToken, { data: values }).then(
          (response) => responseProcessorProxy(response)
        );
      } else {
        UpdateCategory(uuid, user.idToken, { data: values }).then((response) =>
          responseProcessorProxy(response)
        );
      }
    },
  });

  const loadData = useCallback(() => {
    setLinkedContent((prev) => ({...prev, loading: true}));

    if (!newCategory) {
      setIsLoading(true);

      GetCategory(uuid, user.idToken)
        .then((res) => res.json())
        .then((res) => {
          setCurrentDetail({
            id: res?.data?.id,
            name: res?.data?.name,
            active: res?.data?.active,
            sort_order: res?.data?.sort_order,
            image_url: res?.data?.image_url,
            tags: res?.data?.tags
          });

          setMetadata(res?.data?.media_metadata || {});

          setLinkedContent({
            loading: false,
            data: res?.data?.linked_content,
            count: res?.data?.linked_content?.length
          })
          setIsLoading(false);
        })
        .catch((err) => console.error(err));
    }
  }, [newCategory, user, uuid])

  useEffect(() => {
    loadData();
  }, [loadData]);

  return (
    <Page links={links} title={`${currentAction} Category`}>
      <EntryForm
        isLoading={isLoading}
        formik={formik}
        submitHandler={formik.handleSubmit}
        cancelURL={`/categories`}
      >
        <Column>
          <Input name="name" type="text" label="Category Name" formik={formik} />
          <MediaUploader
            name="image_url"
            type="text"
            label="Image URL"
            formik={formik}
            metadata={metadata}
            mediaType="image"
          />
        </Column>
        <Column>
          <DataSelector
            name="tags"
            formik={formik}
            label="Tags"
            resourceEndpoint="tags?category=Content"
            filterOptions={(values) => values.filter((a) => !a.is_expired)}
          />
          <Input name="sort_order" type="number" label="Sort Order" formik={formik} />
          <CheckBox name="active" label="Active" formik={formik} />
        </Column>
    </EntryForm>

    {
      !newCategory && (
        <LinkedContentList
          categoryId={uuid}
          linkedContent={linkedContent}
          resetList={loadData}
        />
      )
    }
    </Page>
  )
}

export default CategoryDetail